import { DrawingSingleton } from "../drawing/DrawingSingleton";
import { Logger } from "../logger";

export type MemorySnapshot = {
  wasmUsed: number;    // ODA HpTrc.Usd if available, else 0
  wasmTotal: number;   // WASM linear memory size (bytes)
  jsHeap: number;      // performance.memory.usedJSHeapSize (0 if unavailable)
  jsHeapLimit: number; // performance.memory.jsHeapSizeLimit (0 if unavailable, e.g. Safari/iOS)
  timestamp: number;
  drawingId?: number;
};

type MemoryDelta = {
  wasmUsedDelta: number;
  wasmTotalGrew: boolean;
  jsHeapDelta: number;
  elapsedMs: number;
  baseline: MemorySnapshot;
  current: MemorySnapshot;
};

const WARN_MB = 50;
const ERROR_MB = 100;
const CONSECUTIVE_GROWTH_ALERT = 3;

// Fenêtre de confirmation avant toute remontée Sentry — cf. _confirmThenReport().
const CONFIRM_WINDOW_MS = 20_000;
const CONFIRM_SAMPLE_MS = 5_000;
// Le tas doit redescendre d'au moins 10% sous le niveau de déclenchement pour
// qu'on considère que le GC a récupéré. En dessous c'est du bruit de mesure
// (performance.memory est quantifié), pas une libération.
const CONFIRM_RECLAIM_RATIO = 0.9;

// Resolved once on first successful read, stable after that
let _wasmTotalFn: (() => number) | null = null;
let _wasmUsedFn: (() => number) | null = null;
let _probed = false;

function probeModule(mod: any): void {
  if (_probed) return;
  _probed = true;

  // ── WASM total linear memory ──────────────────────────────────────────────
  // Try each candidate in order of reliability
  if (typeof mod.HEAP8?.buffer?.byteLength === "number") {
    _wasmTotalFn = () => mod.HEAP8.buffer.byteLength;
  } else if (typeof mod.HEAPU8?.buffer?.byteLength === "number") {
    _wasmTotalFn = () => mod.HEAPU8.buffer.byteLength;
  } else if (mod.wasmMemory instanceof WebAssembly.Memory) {
    _wasmTotalFn = () => mod.wasmMemory.buffer.byteLength;
  } else if (typeof mod.TOTAL_MEMORY === "number") {
    // Static value set at init — won't reflect growth but better than 0
    _wasmTotalFn = () => mod.TOTAL_MEMORY;
  }

  // ── WASM used (ODA-specific heap tracker) ─────────────────────────────────
  if (typeof mod.HpTrc?.Usd === "number") {
    _wasmUsedFn = () => mod.HpTrc.Usd;
  }
  // No standard Emscripten equivalent — HpTrc is ODA-only

  if (_wasmTotalFn || _wasmUsedFn) {
    console.debug(
      "[MemoryMonitor] WASM sources resolved —",
      _wasmTotalFn ? "total ✓" : "total ✗",
      _wasmUsedFn ? "used ✓" : "used ✗"
    );
  } else {
    console.warn(
      "[MemoryMonitor] No WASM memory source found on module. " +
        "Call MemoryMonitor.diagnose() to inspect available properties."
    );
  }
}

export class MemoryMonitor {
  private static _sessionBaseline: MemorySnapshot | null = null; // set once, never reset
  private static _baseline: MemorySnapshot | null = null;        // reset on each drawing open
  private static _lastSnapshot: MemorySnapshot | null = null;
  private static _timer: ReturnType<typeof setInterval> | null = null;
  private static _consecutiveGrowth = 0;
  private static _suppressedUntil = 0;
  /** Fenêtres de confirmation en cours, une par signal — cf. _confirmThenReport(). */
  private static _pendingReports = new Map<
    string,
    ReturnType<typeof setInterval>
  >();

  /**
   * Le save sérialise le dessin entier (Blob + ArrayBuffer) pendant que la
   * scène WASM live reste résidente : un doublement transitoire de la mémoire
   * est donc attendu à *chaque* sauvegarde, pas seulement avant un reload.
   * Sans ça, un tick de sampling qui tombe pendant ce pic déclenche un faux
   * positif Sentry pour un pic qui aurait disparu à la mesure suivante.
   * `saveDrawing()` arme cette fenêtre autour de la sérialisation.
   */
  static suppressReportingFor(ms: number): void {
    this._suppressedUntil = Math.max(this._suppressedUntil, Date.now() + ms);
  }

  private static _isSuppressed(): boolean {
    return Date.now() < this._suppressedUntil;
  }

  static snapshot(drawingId?: number): MemorySnapshot {
    let wasmUsed = 0;
    let wasmTotal = 0;
    try {
      const mod = DrawingSingleton.module as any;
      probeModule(mod);
      wasmUsed = _wasmUsedFn?.() ?? 0;
      wasmTotal = _wasmTotalFn?.() ?? 0;
    } catch {
      // module not yet loaded — values stay 0
    }
    return {
      wasmUsed,
      wasmTotal,
      jsHeap: (performance as any).memory?.usedJSHeapSize ?? 0,
      jsHeapLimit: (performance as any).memory?.jsHeapSizeLimit ?? 0,
      timestamp: Date.now(),
      drawingId,
    };
  }

  /** Call from the browser console to inspect what the ODA module exposes. */
  static diagnose(): void {
    let mod: any;
    try {
      mod = DrawingSingleton.module;
    } catch {
      console.error("[MemoryMonitor.diagnose] Module not loaded yet.");
      return;
    }

    const candidates = [
      "HEAP8", "HEAPU8", "HEAP16", "HEAP32", "HEAPF32", "HEAPF64",
      "wasmMemory", "TOTAL_MEMORY", "HpTrc",
    ];
    const found: Record<string, unknown> = {};
    for (const key of candidates) {
      const val = mod[key];
      if (val !== undefined) found[key] = val;
    }

    console.group("[MemoryMonitor.diagnose]");
    console.log("Known candidates present:", found);

    // Also dump all top-level keys that look memory-related
    const allKeys = Object.keys(mod).filter(
      (k) => /heap|mem|hptr|hptrc|total|wasm/i.test(k)
    );
    console.log("All memory-like keys on module:", allKeys);
    console.groupEnd();
  }

  static baseline(drawingId?: number): void {
    const snap = this.snapshot(drawingId);

    // Session baseline: set once across the whole app lifetime
    if (!this._sessionBaseline) {
      this._sessionBaseline = snap;
      Logger.breadcrumb("memory.session-baseline", {
        heap_mb: mb(primaryHeap(snap)),
        drawingId,
      });
    }

    this._baseline = snap;
    this._lastSnapshot = snap;
    this._consecutiveGrowth = 0;
    Logger.breadcrumb("memory.baseline", {
      heap_mb: mb(primaryHeap(snap)),
      session_delta_mb: mb(primaryHeap(snap) - primaryHeap(this._sessionBaseline)),
      drawingId,
    });
  }

  static delta(): MemoryDelta | null {
    if (!this._baseline) return null;
    const current = this.snapshot(this._baseline.drawingId);
    return {
      wasmUsedDelta: current.wasmUsed - this._baseline.wasmUsed,
      wasmTotalGrew: current.wasmTotal > this._baseline.wasmTotal,
      jsHeapDelta: current.jsHeap - this._baseline.jsHeap,
      elapsedMs: current.timestamp - this._baseline.timestamp,
      baseline: this._baseline,
      current,
    };
  }

  /** Cumulative heap drift since the first drawing was opened. Never resets. */
  static sessionDelta(): number | null {
    if (!this._sessionBaseline) return null;
    const current = this.snapshot();
    return primaryHeap(current) - primaryHeap(this._sessionBaseline);
  }

  static startSampling(intervalMs = 60_000, drawingId?: number): void {
    this.stopSampling();
    this._timer = setInterval(() => this._tick(drawingId), intervalMs);
  }

  /**
   * N'annule volontairement PAS les fenêtres de confirmation en cours : les deux
   * appelants (Facade.dispose(), FileManager.close()) enchaînent reportOnClose()
   * puis stopSampling(), donc les purger ici annulerait systématiquement l'alerte
   * de fermeture. C'est justement à cet instant que la fenêtre est la plus
   * informative — elle observe si fermer le dessin libère la mémoire. Elles
   * s'arrêtent seules au bout de CONFIRM_WINDOW_MS et ne font que lire
   * performance.memory, sans toucher au module déchargé (cf. snapshot()).
   */
  static stopSampling(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  static reportOnClose(drawingId?: number): void {
    const d = this.delta();
    if (!d) return;

    const sessionDriftMb = this._sessionBaseline
      ? mb(primaryHeap(d.current) - primaryHeap(this._sessionBaseline))
      : null;

    Logger.breadcrumb("memory.on-close", {
      drawing_delta_mb: mb(primaryHeap(d.current) - primaryHeap(d.baseline)),
      session_delta_mb: sessionDriftMb,
      heap_mb: mb(primaryHeap(d.current)),
      elapsed_min: Math.round(d.elapsedMs / 60_000),
      drawingId,
    });

    if (d.wasmTotalGrew) {
      Logger.warn(
        `MemoryMonitor: WASM linear memory grew — ${mb(d.baseline.wasmTotal)}MB → ${mb(d.current.wasmTotal)}MB`
      );
    }

    if (sessionDriftMb !== null && sessionDriftMb >= ERROR_MB && !this._isSuppressed()) {
      this._confirmThenReport(
        "session-drift",
        `Heap +${sessionDriftMb}MB cumulative across drawings`,
        {
          session_delta_mb: sessionDriftMb,
          heap_mb: mb(primaryHeap(d.current)),
          drawingId,
        }
      );
    }

    this._checkThresholds(d, drawingId);
  }

  // ── Private ──────────────────────────────────────────────────────────────

  /**
   * Ne remonte à Sentry que si la mémoire s'installe réellement à ce niveau.
   *
   * Un seuil franchi ne prouve rien à l'instant T : le pic peut n'être qu'un lot
   * d'objets pas encore collectés. On ré-échantillonne pendant CONFIRM_WINDOW_MS —
   * si le tas redescend nettement sous le niveau de déclenchement, le GC a fait son
   * travail et on ne remonte rien ; s'il reste au niveau ou monte, c'est de la
   * rétention et on remonte, enrichi du profil observé pendant la fenêtre.
   *
   * Complémentaire de suppressReportingFor() : celle-ci couvre les pics dont on
   * connaît déjà la cause (le save), celle-là attrape tous les autres a posteriori.
   *
   * La source (WASM ou jsHeap) est figée au déclenchement : si elle disparaît en
   * cours de route (module déchargé à la fermeture du dessin), l'échantillon est
   * ignoré plutôt que lu comme une chute à zéro, qui retracterait à tort.
   */
  private static _confirmThenReport(
    key: string,
    message: string,
    context: Record<string, unknown>
  ): void {
    // Un seul guet par signal : tant que la mémoire est haute le seuil retombe à
    // chaque tick, sans ça on empilerait une fenêtre (et une erreur) par tick.
    if (this._pendingReports.has(key)) return;

    const trip = this.snapshot();
    const readHeap =
      trip.wasmUsed > 0
        ? (s: MemorySnapshot) => s.wasmUsed
        : (s: MemorySnapshot) => s.jsHeap;
    const tripHeap = readHeap(trip);

    // Aucune source mesurable : on ne peut pas confirmer. On remonte plutôt que de
    // perdre l'alerte — ce chemin ne doit jamais avaler un signal en silence.
    if (tripHeap <= 0) {
      Logger.captureException(new Error(message), context);
      return;
    }

    let minHeap = tripHeap;
    let lastHeap = tripHeap;
    let samplesLeft = Math.max(
      1,
      Math.round(CONFIRM_WINDOW_MS / CONFIRM_SAMPLE_MS)
    );

    const timer = setInterval(() => {
      const heap = readHeap(this.snapshot());
      if (heap > 0) {
        lastHeap = heap;
        if (heap < minHeap) minHeap = heap;
      }

      if (--samplesLeft > 0) return;

      clearInterval(timer);
      this._pendingReports.delete(key);

      const profile = {
        ...context,
        trip_heap_mb: mb(tripHeap),
        min_heap_mb: mb(minHeap),
        final_heap_mb: mb(lastHeap),
        confirm_window_s: Math.round(CONFIRM_WINDOW_MS / 1000),
      };

      if (minHeap < tripHeap * CONFIRM_RECLAIM_RATIO) {
        // Breadcrumb plutôt que rien : si une vraie erreur tombe plus tard, on veut
        // voir dans son fil les alertes qui s'étaient résorbées avant elle.
        Logger.breadcrumb("memory.alert-reclaimed", { message, ...profile });
        return;
      }

      Logger.captureException(new Error(message), profile);
    }, CONFIRM_SAMPLE_MS);

    this._pendingReports.set(key, timer);
  }

  private static _tick(drawingId?: number): void {
    // A save (auto or manual) is known to spike memory transiently — see
    // suppressReportingFor(). Skip the whole sample rather than let a peak
    // poison _lastSnapshot/_consecutiveGrowth for comparisons made once the
    // suppression window has ended.
    if (this._isSuppressed()) return;

    const snap = this.snapshot(drawingId);

    // Use wasmUsed when available, jsHeap as fallback (Chrome jsHeap includes WASM linear memory)
    const used = (s: MemorySnapshot) => s.wasmUsed || s.jsHeap;

    if (this._lastSnapshot && used(snap) > used(this._lastSnapshot)) {
      this._consecutiveGrowth++;
      if (this._consecutiveGrowth >= CONSECUTIVE_GROWTH_ALERT) {
        const source = snap.wasmUsed ? "WASM" : "JS";
        this._confirmThenReport(
          "monotonic-growth",
          `${source} heap growing monotonically`,
          {
            consecutive_samples: this._consecutiveGrowth,
            heap_used_mb: mb(used(snap)),
            heap_delta_mb: this._baseline
              ? mb(used(snap) - used(this._baseline))
              : null,
            source,
            drawingId,
          }
        );
        this._consecutiveGrowth = 0;
      }
    } else {
      this._consecutiveGrowth = 0;
    }

    this._lastSnapshot = snap;
    const d = this.delta();
    if (d) this._checkThresholds(d, drawingId);
  }

  private static _checkThresholds(d: MemoryDelta, drawingId?: number): void {
    // Prefer WASM delta; fall back to jsHeap delta when WASM is not available
    const rawDelta = d.wasmUsedDelta !== 0 ? d.wasmUsedDelta : d.jsHeapDelta;
    const source = d.wasmUsedDelta !== 0 ? "WASM" : "JS";
    const deltaMb = mb(rawDelta);
    if (deltaMb >= ERROR_MB && !this._isSuppressed()) {
      this._confirmThenReport(
        "drawing-delta",
        `${source} heap +${deltaMb}MB since drawing open`,
        {
          heap_delta_mb: deltaMb,
          heap_used_mb: mb(d.wasmUsedDelta !== 0 ? d.current.wasmUsed : d.current.jsHeap),
          elapsed_min: Math.round(d.elapsedMs / 60_000),
          source,
          drawingId,
        }
      );
    } else if (deltaMb >= WARN_MB) {
      Logger.warn(
        `MemoryMonitor: +${deltaMb}MB ${source} heap since drawing open (drawingId=${drawingId})`
      );
    }
  }
}

function mb(bytes: number): number {
  return Math.round(bytes / 1024 / 1024);
}

/** WASM used when available, jsHeap otherwise (Chrome jsHeap includes WASM linear memory). */
function primaryHeap(s: MemorySnapshot): number {
  return s.wasmUsed || s.jsHeap;
}
